import type { ActiveFilter, TableFilterConfig, TableFilterOption } from "../../types/TableFilter.types";
import { useI18n } from "vue-i18n";
import { TableFilterCondition, TableFilterType } from "../../types/TableFilter.types";
import { isFilterSet } from "./useTableFilters";

/** A list of chosen values reads as names up to two; more read as a count ("3 gekozen"). */
const MAX_LISTED_VALUES = 2;

const CONDITION_KEYS: Record<TableFilterCondition, string> = {
  [TableFilterCondition.EQUALS]: "filters.conditions.equals",
  [TableFilterCondition.NOT_EQUALS]: "filters.conditions.not_equals",
  [TableFilterCondition.CONTAINS]: "filters.conditions.contains",
  [TableFilterCondition.NOT_CONTAINS]: "filters.conditions.not_contains",
  [TableFilterCondition.STARTS_WITH]: "filters.conditions.starts_with",
  [TableFilterCondition.ENDS_WITH]: "filters.conditions.ends_with",
  [TableFilterCondition.GREATER_THAN]: "filters.conditions.greater_than",
  [TableFilterCondition.GREATER_THAN_OR_EQUAL]: "filters.conditions.greater_than_or_equal",
  [TableFilterCondition.LESS_THAN]: "filters.conditions.less_than",
  [TableFilterCondition.LESS_THAN_OR_EQUAL]: "filters.conditions.less_than_or_equal",
  [TableFilterCondition.BETWEEN]: "filters.conditions.between",
  [TableFilterCondition.IS_TRUE]: "filters.true",
  [TableFilterCondition.IS_FALSE]: "filters.false",
  [TableFilterCondition.IS_NULL]: "filters.conditions.is_null",
  [TableFilterCondition.IS_NOT_NULL]: "filters.conditions.is_not_null",
};

/**
 * The label a yes/no filter gives one of its two values through `options` (e.g. "Actief" and "Inactief" for a
 * status), or null when it uses the default "Ja" and "Nee".
 */
export function booleanFilterLabel(config: Pick<TableFilterConfig, "options">, value: boolean): string | null {
  if (!Array.isArray(config.options)) {
    return null;
  }
  return config.options.find(option => option.value === value)?.label ?? null;
}

/**
 * The short text of a filter's value, as the filter panel shows it next to the filter and a chip in the toolbar
 * shows it after the filter's name: "Actief", "Park manager, Aangepast", "3 gekozen", "Alle".
 */
export function useFilterSummary() {
  const { t, d } = useI18n();

  function optionLabel(options: TableFilterOption[], value: unknown): string {
    const option = options.find(item => String(item.value) === String(value));
    return option ? option.label : String(value);
  }

  function listLabel(labels: string[]): string {
    if (labels.length <= MAX_LISTED_VALUES) {
      return labels.join(", ");
    }
    return t("filters.summary.chosen", { count: labels.length });
  }

  function dateLabel(value: unknown): string {
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
      return String(value);
    }
    return d(value, { day: "numeric", month: "short", year: "numeric" });
  }

  function conditionPrefix(condition: TableFilterCondition, text: string): string {
    return `${t(CONDITION_KEYS[condition])} ${text}`;
  }

  /** The value of a filter, or "Alle" when it does not narrow the list. */
  function summarize(
    config: TableFilterConfig,
    filter: Pick<ActiveFilter, "condition" | "value"> | undefined,
    options: TableFilterOption[] = [],
  ): string {
    if (!filter || !isFilterSet(filter)) {
      return t("filters.summary.all");
    }
    const { condition, value } = filter;
    if (condition === TableFilterCondition.IS_NULL || condition === TableFilterCondition.IS_NOT_NULL) {
      return t(CONDITION_KEYS[condition]);
    }

    switch (config.type) {
      case TableFilterType.BOOLEAN:
        return booleanFilterLabel(config, value === true) ?? (value ? t("filters.true") : t("filters.false"));
      case TableFilterType.MULTI_SELECT: {
        const labels = (value as Array<string | number>).map(item => optionLabel(options, item));
        const text = listLabel(labels);
        return condition === TableFilterCondition.NOT_CONTAINS ? conditionPrefix(condition, text) : text;
      }
      case TableFilterType.SELECT: {
        const text = optionLabel(options, value);
        return condition === TableFilterCondition.NOT_EQUALS ? conditionPrefix(condition, text) : text;
      }
      case TableFilterType.DATE_RANGE: {
        if (Array.isArray(value)) {
          return t("filters.summary.range", { from: dateLabel(value[0]), to: dateLabel(value[1]) });
        }
        if (condition === TableFilterCondition.GREATER_THAN) {
          return `${t("filters.conditions.after")} ${dateLabel(value)}`;
        }
        if (condition === TableFilterCondition.LESS_THAN) {
          return `${t("filters.conditions.before")} ${dateLabel(value)}`;
        }
        return dateLabel(value);
      }
      case TableFilterType.NUMBER_RANGE: {
        if (Array.isArray(value)) {
          return t("filters.summary.range", { from: String(value[0]), to: String(value[1]) });
        }
        return condition === TableFilterCondition.EQUALS ? String(value) : conditionPrefix(condition, String(value));
      }
      case TableFilterType.TEXT:
      default: {
        const text = `"${String(value)}"`;
        return condition === TableFilterCondition.CONTAINS ? text : conditionPrefix(condition, text);
      }
    }
  }

  return { summarize };
}
